'use client'

import { memo, useCallback, useEffect, useMemo } from 'react'
import Joyride, { ACTIONS, type CallBackProps, EVENTS, STATUS } from 'react-joyride'
import { useTourStore } from '../stores/useTourStore'
import { tourRegistry } from '../tours'

function TourRendererComponent() {
  const { currentTour, stepIndex, run, nextStep, completeTour, manualStepAdvance, resetAllTours } = useTourStore()

  const currentTourConfig = useMemo(() => {
    return currentTour ? tourRegistry[currentTour] : null
  }, [currentTour])

  // Dynamic button text - only for welcome tour specifically
  const getLastButtonText = useCallback(() => {
    if (currentTour === 'welcome' && stepIndex >= (currentTourConfig?.steps.length || 0) - 1) {
      return 'Get Started'
    }
    return 'Finish'
  }, [currentTour, stepIndex, currentTourConfig?.steps.length])

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { action, index, status, type } = data

      // Tour finished or skipped
      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
        completeTour()
        return
      }

      // Handle step progression
      if (type === EVENTS.STEP_AFTER) {
        const currentStep = currentTourConfig?.steps[index]

        // Steps that require manual action should not auto-advance
        if (currentStep?.requiresUserAction) {
          return
        }

        // Auto-advance for normal steps
        if (action === ACTIONS.NEXT) {
          nextStep()
        }
      }

      // Target not found - skip to next after delay
      if (type === EVENTS.TARGET_NOT_FOUND) {
        setTimeout(() => {
          nextStep()
        }, 500)
      }

      // Handle close/escape
      if (action === ACTIONS.CLOSE) {
        resetAllTours()
      }
    },
    [currentTourConfig, nextStep, completeTour, resetAllTours],
  )

  // Reset tour if currentTour becomes null but run is still true
  useEffect(() => {
    if (!currentTour && run) {
      resetAllTours()
    }
  }, [currentTour, run, resetAllTours])

  const styles = useMemo(
    () => ({
      options: {
        zIndex: 10000,
      },
      tooltip: {
        maxWidth: 400,
        padding: '12px 16px',
        borderRadius: 8,
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
        fontSize: 14,
        lineHeight: '1.5',
      },
      floater: {
        arrow: {
          length: 8,
          spread: 16,
        },
      },
      spotlight: {
        borderRadius: 4,
      },
    }),
    [],
  )

  const locale = useMemo(
    () => ({
      back: 'Back',
      close: 'Got it',
      last: getLastButtonText(),
      next: 'Next',
      open: 'Open the dialog',
      skip: 'Skip',
    }),
    [getLastButtonText],
  )

  // If there's no active tour, don't render the component
  // Note: We always render tours when active, the feature flag only controls the debug modal (TourStarter)
  if (!currentTourConfig || !run) {
    return null
  }

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous={true}
      run={run}
      stepIndex={stepIndex}
      steps={currentTourConfig.steps}
      showProgress={false}
      showSkipButton={currentTour !== 'welcome' && currentTour !== 'dashboard'}
      disableOverlayClose={true}
      disableScrolling={false}
      hideBackButton={true}
      spotlightClicks={true}
      disableOverlay={false}
      debug={false}
      styles={styles}
      locale={locale}
    />
  )
}

// Memoize the entire component
export const TourRenderer = memo(TourRendererComponent)
